import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { defaultTemplateValues, defaultUsagePlanId } from "./defaults";
import { getFirebaseServices } from "./firebase";
import type {
  AppData,
  AuditLog,
  BusinessAccount,
  BillingStatus,
  MessageTemplate,
  CsvValidationResult,
  UploadDetails,
  UploadRecord,
  UploadRow,
  UsagePlanId,
} from "./types";

export async function loadAppData(userId: string, userEmail: string | null): Promise<AppData> {
  const account = await ensureBusinessAccount(userId, userEmail);
  const templates = account ? await ensureMessageTemplates(account.id) : null;
  const [uploads, auditLogs] = account
    ? await Promise.all([loadRecentUploads(account.id), loadAuditLogs(account.id)])
    : [[], []];

  return {
    account,
    templates,
    uploads,
    auditLogs,
  };
}

export async function saveBusinessProfile(
  accountId: string,
  values: {
    business_name: string;
    business_address: string;
    business_phone: string;
    contact_name: string;
    primary_email: string;
    google_review_url: string;
    facebook_review_url: string;
    usage_plan_id: UsagePlanId;
    resend_suppression_days: number;
  },
) {
  const { firestore } = requiredServices();
  const accountRef = accountDoc(accountId);
  const existing = await getDoc(accountRef);
  const existingAccount = accountFromDoc(accountId, existing.data() ?? {});
  const nextAccount: BusinessAccount = {
    ...existingAccount,
    ...values,
    billing_provider: existingAccount.billing_provider,
    billing_status: existingAccount.billing_status,
    resend_suppression_days: values.resend_suppression_days,
    is_profile_complete: false,
    updated_at: nowIso(),
  };
  const isProfileComplete = isBusinessProfileComplete(nextAccount);
  const updatedAt = nowIso();

  await updateDoc(accountRef, {
    ...values,
    is_profile_complete: isProfileComplete,
    updated_at: updatedAt,
  });

  await insertAuditLog(accountId, "business_profile_saved", {
    is_profile_complete: isProfileComplete,
  });

  const updated = await getDoc(doc(firestore, "accounts", accountId));
  return accountFromDoc(accountId, updated.data() ?? {});
}

export async function saveValidatedUpload(
  accountId: string,
  userId: string,
  file: File,
  validation: CsvValidationResult,
): Promise<UploadDetails> {
  const { firestore, storage } = requiredServices();
  const storagePath = buildUploadStoragePath(userId, file.name);
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, {
    contentType: file.type || "text/csv",
  });

  const uploadRef = doc(collection(firestore, "accounts", accountId, "uploads"));
  const upload: UploadRecord = {
    id: uploadRef.id,
    business_account_id: accountId,
    original_filename: file.name,
    storage_path: storagePath,
    total_rows: validation.summary.totalRows,
    valid_rows: validation.summary.validRows,
    rejected_rows: validation.summary.rejectedRows,
    queued_rows: 0,
    suppressed_rows: 0,
    status: "validated",
    created_at: nowIso(),
    started_at: null,
  };

  await setDoc(uploadRef, upload);

  const savedRows: UploadRow[] = validation.rows.map((row) => ({
    ...row,
    upload_id: upload.id,
  }));
  await writeUploadRows(accountId, upload.id, savedRows);

  await insertAuditLog(accountId, "upload_validated", {
    file_name: file.name,
    storage_path: storagePath,
    total_rows: validation.summary.totalRows,
    valid_rows: validation.summary.validRows,
    rejected_rows: validation.summary.rejectedRows,
  });

  return {
    upload,
    rows: savedRows,
  };
}

export async function loadUploadDetails(accountId: string, uploadId: string): Promise<UploadDetails> {
  const { firestore } = requiredServices();
  const uploadRef = doc(firestore, "accounts", accountId, "uploads", uploadId);
  const uploadSnapshot = await getDoc(uploadRef);

  if (!uploadSnapshot.exists()) {
    throw new Error("Upload record was not found.");
  }

  const rowsQuery = query(
    collection(firestore, "accounts", accountId, "uploads", uploadId, "rows"),
    orderBy("row_number", "asc"),
  );
  const rowsSnapshot = await getDocs(rowsQuery);

  return {
    upload: uploadFromDoc(accountId, uploadSnapshot.id, uploadSnapshot.data()),
    rows: rowsSnapshot.docs.map((rowDoc) => uploadRowFromDoc(rowDoc.id, rowDoc.data())),
  };
}

export async function markUploadReady(accountId: string, uploadId: string): Promise<UploadDetails> {
  const { firestore } = requiredServices();
  const accountSnapshot = await getDoc(accountDoc(accountId));
  const account = accountFromDoc(accountId, accountSnapshot.data() ?? {});
  const details = await loadUploadDetails(accountId, uploadId);

  if (details.upload.status !== "validated") {
    return details;
  }

  const now = nowIso();
  const candidates = details.rows.filter((row) => row.status === "valid" && row.normalized_phone_number);
  const histories = new Map<string, Record<string, unknown>>();

  await Promise.all(
    candidates.map(async (row) => {
      const normalized = row.normalized_phone_number!;
      if (histories.has(normalized)) {
        return;
      }
      const historySnapshot = await getDoc(phoneHistoryDoc(accountId, normalized));
      histories.set(normalized, historySnapshot.data() ?? {});
    }),
  );

  let queuedRows = 0;
  let suppressedRows = 0;
  const rowWrites: Array<Promise<void>> = [];

  details.rows.forEach((row) => {
    if (row.status !== "valid" || !row.normalized_phone_number || !row.id) {
      return;
    }

    const history = histories.get(row.normalized_phone_number) ?? {};
    const lastContactedAt = nullableString(history.last_contacted_at);
    const suppressionReason = getSuppressionReason(
      lastContactedAt,
      account.resend_suppression_days,
      now,
    );
    const rowRef = doc(firestore, "accounts", accountId, "uploads", uploadId, "rows", row.id);

    if (suppressionReason) {
      suppressedRows += 1;
      rowWrites.push(updateDoc(rowRef, {
        status: "rejected_suppressed",
        rejection_reason: suppressionReason,
      }));
      return;
    }

    queuedRows += 1;
    rowWrites.push(updateDoc(rowRef, {
      status: "queued",
      rejection_reason: null,
      queued_at: now,
    }));
    rowWrites.push(setDoc(
      phoneHistoryDoc(accountId, row.normalized_phone_number),
      {
        phone_number: row.normalized_phone_number,
        last_contacted_at: now,
        last_upload_id: uploadId,
        last_status: "queued",
        total_contact_count: increment(1),
        updated_at: now,
        created_at: stringValue(history.created_at, now),
      },
      { merge: true },
    ));
  });

  const uploadRef = doc(firestore, "accounts", accountId, "uploads", uploadId);
  await Promise.all(rowWrites);
  await updateDoc(uploadRef, {
    status: "ready",
    valid_rows: queuedRows,
    rejected_rows: details.upload.total_rows - queuedRows,
    queued_rows: queuedRows,
    suppressed_rows: suppressedRows,
    started_at: now,
  });

  await insertAuditLog(accountId, "upload_marked_ready", {
    upload_id: uploadId,
    queued_rows: queuedRows,
    suppressed_rows: suppressedRows,
    resend_suppression_days: account.resend_suppression_days,
  });

  return loadUploadDetails(accountId, uploadId);
}

async function ensureBusinessAccount(userId: string, userEmail: string | null) {
  const accountRef = accountDoc(userId);
  const existing = await getDoc(accountRef);

  if (existing.exists()) {
    return accountFromDoc(existing.id, existing.data());
  }

  const timestamp = nowIso();
  const account: BusinessAccount = {
    id: userId,
    owner_user_id: userId,
    business_name: "",
    business_address: "",
    business_phone: "",
    contact_name: "",
    primary_email: userEmail,
    google_review_url: "",
    facebook_review_url: "",
    usage_plan_id: defaultUsagePlanId,
    billing_provider: "square",
    billing_status: "not_configured",
    resend_suppression_days: 90,
    is_profile_complete: false,
    created_at: timestamp,
    updated_at: timestamp,
  };

  await setDoc(accountRef, account);
  await insertAuditLog(account.id, "business_account_created", {});
  return account;
}

async function ensureMessageTemplates(accountId: string) {
  const templateRef = messageTemplateDoc(accountId);
  const existing = await getDoc(templateRef);

  if (existing.exists()) {
    return templateFromDoc(accountId, existing.data());
  }

  const timestamp = nowIso();
  const template: MessageTemplate = {
    business_account_id: accountId,
    greeting: defaultTemplateValues.greeting,
    satisfaction_question: defaultTemplateValues.satisfactionQuestion,
    unhappy_message: defaultTemplateValues.unhappyMessage,
    final_thanks: defaultTemplateValues.finalThanks,
    created_at: timestamp,
    updated_at: timestamp,
  };

  await setDoc(templateRef, template);
  await insertAuditLog(accountId, "message_templates_created", {});
  return template;
}

async function loadRecentUploads(accountId: string) {
  const { firestore } = requiredServices();
  const uploadsQuery = query(
    collection(firestore, "accounts", accountId, "uploads"),
    orderBy("created_at", "desc"),
    limit(5),
  );
  const snapshot = await getDocs(uploadsQuery);

  return snapshot.docs.map((uploadDoc) =>
    uploadFromDoc(accountId, uploadDoc.id, uploadDoc.data()),
  );
}

async function loadAuditLogs(accountId: string) {
  const { firestore } = requiredServices();
  const logsQuery = query(
    collection(firestore, "accounts", accountId, "audit_logs"),
    orderBy("created_at", "desc"),
    limit(10),
  );
  const snapshot = await getDocs(logsQuery);

  return snapshot.docs.map((logDoc) => auditLogFromDoc(accountId, logDoc.id, logDoc.data()));
}

async function insertAuditLog(
  accountId: string,
  eventType: string,
  eventPayload: Record<string, unknown>,
) {
  const { firestore, auth } = requiredServices();
  const logRef = doc(collection(firestore, "accounts", accountId, "audit_logs"));
  const log: AuditLog = {
    id: logRef.id,
    business_account_id: accountId,
    actor_user_id: auth.currentUser?.uid ?? null,
    event_type: eventType,
    event_payload: eventPayload,
    created_at: nowIso(),
  };

  await setDoc(logRef, log);
}

async function writeUploadRows(accountId: string, uploadId: string, rows: UploadRow[]) {
  const { firestore } = requiredServices();
  const chunkSize = 500;

  for (let index = 0; index < rows.length; index += chunkSize) {
    const batch = writeBatch(firestore);
    const chunk = rows.slice(index, index + chunkSize);

    chunk.forEach((row) => {
      const rowRef = doc(collection(firestore, "accounts", accountId, "uploads", uploadId, "rows"));
      row.id = rowRef.id;
      batch.set(rowRef, row);
    });

    await batch.commit();
  }
}

function requiredServices() {
  const services = getFirebaseServices();
  if (!services) {
    throw new Error("Firebase environment variables are not configured.");
  }

  return services;
}

function accountDoc(accountId: string) {
  const { firestore } = requiredServices();
  return doc(firestore, "accounts", accountId);
}

function messageTemplateDoc(accountId: string) {
  const { firestore } = requiredServices();
  return doc(firestore, "accounts", accountId, "message_templates", "default");
}

function phoneHistoryDoc(accountId: string, normalizedPhoneNumber: string) {
  const { firestore } = requiredServices();
  const id = normalizedPhoneNumber.replace(/\D/g, "");
  return doc(firestore, "accounts", accountId, "phone_history", id);
}

function accountFromDoc(id: string, data: Record<string, unknown>): BusinessAccount {
  const account: BusinessAccount = {
    id,
    owner_user_id: stringValue(data.owner_user_id, id),
    business_name: stringValue(data.business_name),
    business_address: stringValue(data.business_address),
    business_phone: stringValue(data.business_phone),
    contact_name: stringValue(data.contact_name),
    primary_email: nullableString(data.primary_email),
    google_review_url: nullableString(data.google_review_url),
    facebook_review_url: nullableString(data.facebook_review_url),
    usage_plan_id: usagePlanIdValue(data.usage_plan_id),
    billing_provider: "square",
    billing_status: billingStatusValue(data.billing_status),
    resend_suppression_days: nonNegativeNumberValue(data.resend_suppression_days, 90),
    is_profile_complete: false,
    created_at: stringValue(data.created_at, nowIso()),
    updated_at: stringValue(data.updated_at, nowIso()),
  };

  return {
    ...account,
    is_profile_complete: isBusinessProfileComplete(account),
  };
}

function templateFromDoc(accountId: string, data: Record<string, unknown>): MessageTemplate {
  return {
    business_account_id: accountId,
    greeting: stringValue(data.greeting, defaultTemplateValues.greeting),
    satisfaction_question: stringValue(
      data.satisfaction_question,
      defaultTemplateValues.satisfactionQuestion,
    ),
    unhappy_message: stringValue(data.unhappy_message, defaultTemplateValues.unhappyMessage),
    final_thanks: stringValue(data.final_thanks, defaultTemplateValues.finalThanks),
    created_at: stringValue(data.created_at, nowIso()),
    updated_at: stringValue(data.updated_at, nowIso()),
  };
}

function uploadFromDoc(
  accountId: string,
  id: string,
  data: Record<string, unknown>,
): UploadRecord {
  return {
    id,
    business_account_id: stringValue(data.business_account_id, accountId),
    original_filename: stringValue(data.original_filename),
    storage_path: nullableString(data.storage_path),
    total_rows: numberValue(data.total_rows),
    valid_rows: numberValue(data.valid_rows),
    rejected_rows: numberValue(data.rejected_rows),
    queued_rows: numberValue(data.queued_rows),
    suppressed_rows: numberValue(data.suppressed_rows),
    status: uploadStatusValue(data.status),
    created_at: stringValue(data.created_at, nowIso()),
    started_at: nullableString(data.started_at),
  };
}

function uploadRowFromDoc(id: string, data: Record<string, unknown>): UploadRow {
  return {
    id,
    upload_id: nullableString(data.upload_id) ?? undefined,
    row_number: numberValue(data.row_number),
    phone_number: stringValue(data.phone_number),
    normalized_phone_number: nullableString(data.normalized_phone_number) ??
      normalizeStoredPhoneNumber(stringValue(data.phone_number)),
    consent_received_at: stringValue(data.consent_received_at),
    consent_method: stringValue(data.consent_method),
    consent_attested_by: stringValue(data.consent_attested_by),
    status: uploadRowStatusValue(data.status),
    rejection_reason: nullableString(data.rejection_reason),
    queued_at: nullableString(data.queued_at),
    sent_at: nullableString(data.sent_at),
  };
}

function auditLogFromDoc(
  accountId: string,
  id: string,
  data: Record<string, unknown>,
): AuditLog {
  return {
    id,
    business_account_id: stringValue(data.business_account_id, accountId),
    actor_user_id: nullableString(data.actor_user_id),
    event_type: stringValue(data.event_type),
    event_payload: objectValue(data.event_payload),
    created_at: stringValue(data.created_at, nowIso()),
  };
}

function buildUploadStoragePath(userId: string, fileName: string) {
  const safeFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
  return `uploads/${userId}/${Date.now()}-${crypto.randomUUID()}-${safeFileName}`;
}

function nowIso() {
  return new Date().toISOString();
}

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function nullableString(value: unknown) {
  return typeof value === "string" ? value : null;
}

function numberValue(value: unknown) {
  return typeof value === "number" ? value : 0;
}

function normalizeStoredPhoneNumber(value: string) {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }
  return null;
}

function nonNegativeNumberValue(value: unknown, fallback: number) {
  return typeof value === "number" && value >= 0 ? value : fallback;
}

function usagePlanIdValue(value: unknown): UsagePlanId {
  return value === "growth_1000" || value === "scale_5000" || value === "starter_250"
    ? value
    : defaultUsagePlanId;
}

function billingStatusValue(value: unknown): BillingStatus {
  return value === "pending" ||
    value === "active" ||
    value === "past_due" ||
    value === "canceled" ||
    value === "not_configured"
    ? value
    : "not_configured";
}

function isBusinessProfileComplete(account: BusinessAccount) {
  return Boolean(
    account.business_name.trim() &&
      account.business_address.trim() &&
      account.business_phone.trim() &&
      account.contact_name.trim() &&
      account.primary_email?.trim() &&
      account.usage_plan_id &&
      (account.google_review_url?.trim() || account.facebook_review_url?.trim()),
  );
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function uploadStatusValue(value: unknown): UploadRecord["status"] {
  return value === "draft" || value === "ready" || value === "validated"
    ? value
    : "validated";
}

function uploadRowStatusValue(value: unknown): UploadRow["status"] {
  return value === "valid" ||
    value === "queued" ||
    value === "sent" ||
    value === "failed" ||
    value === "rejected_invalid" ||
    value === "rejected_duplicate" ||
    value === "rejected_quota" ||
    value === "rejected_suppressed"
    ? value
    : "rejected_invalid";
}

function getSuppressionReason(lastContactedAt: string | null, suppressionDays: number, now: string) {
  if (!lastContactedAt) {
    return null;
  }
  if (suppressionDays === 0) {
    return "This number has already been contacted by this account.";
  }

  const lastContactedTime = Date.parse(lastContactedAt);
  if (Number.isNaN(lastContactedTime)) {
    return null;
  }

  const elapsedDays = (Date.parse(now) - lastContactedTime) / (24 * 60 * 60 * 1000);
  if (elapsedDays < suppressionDays) {
    return `This number was contacted ${Math.floor(elapsedDays)} days ago, inside the ${suppressionDays}-day resend window.`;
  }

  return null;
}
